import { AuthError, Session, User } from "@supabase/supabase-js"

import supabase from "@/supabase"

import { sendMagicLink } from "./cloudFunctionService"

/**
 * Note: Signup is now handled by the cloud function using the service role key.
 * This function is kept for reference but is no longer used in the main flow.
 */

/**
 * Get the current authenticated user
 */
export const getCurrentUser = async (): Promise<User | null> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    return user
  } catch (error) {
    console.error("Error getting current user:", error)
    return null
  }
}

/**
 * Get the current session
 */
export const getCurrentSession = async (): Promise<Session | null> => {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession()
    return session
  } catch (error) {
    console.error("Error getting current session:", error)
    return null
  }
}

/**
 * Sign out the current user
 */
export const signOut = async (): Promise<{
  success: boolean
  error?: AuthError
}> => {
  try {
    const { error } = await supabase.auth.signOut()
    if (error) {
      return { success: false, error }
    }
    return { success: true }
  } catch (error) {
    console.error("Error signing out:", error)
    return {
      success: false,
      error: error as AuthError,
    }
  }
}

/**
 * Login with magic link and include application ID in the redirect URL
 * This is still used for logging in to existing applications, but not for signup
 * Uses the cloud function to send the magic link instead of direct Supabase OTP
 */
export const loginWithMagicLink = async (
  email: string,
  applicationId: string,
  baseRedirectUrl: string,
): Promise<{ success: boolean; message?: string; error?: AuthError }> => {
  try {
    // Call the cloud function to send the magic link
    const { success, message } = await sendMagicLink(email, applicationId, baseRedirectUrl)

    if (!success) {
      console.error("Error sending login magic link:", message)
      return {
        success: false,
        message: message || "Failed to send login link. Please try again.",
      }
    }

    return {
      success: true,
      message: message || "Login link sent! Please check your email to continue.",
    }
  } catch (error) {
    console.error("Error in loginWithMagicLink:", error)
    return {
      success: false,
      message: "An unexpected error occurred. Please try again.",
      error: error as AuthError,
    }
  }
}
